import { InputAdornment } from '@mui/material';
import { TextField, TextFieldProps } from './text-field';
import { useMemo, useState, useEffect } from 'react';

const stripProtocol = (url: string): string => {
    if (!url || typeof url !== 'string') {
        return '';
    }

    const trimmed = url.trim();

    if (trimmed.startsWith('https://')) {
        return trimmed.slice(8);
    }

    if (trimmed.startsWith('http://')) {
        return trimmed.slice(7);
    }

    return trimmed;
};

const addHttpsProtocol = (url: string): string => {
    if (!url || typeof url !== 'string') {
        return '';
    }

    const trimmed = url.trim();

    if (trimmed.startsWith('https://') || trimmed.startsWith('http://')) {
        return trimmed;
    }

    return `https://${trimmed}`;
};

export const UrlField = ({ maxLength = 1024, ...props }: TextFieldProps) => {
    const displayValue = useMemo(() => {
        return stripProtocol(props.value?.toString() || '');
    }, [props.value]);

    const [internalValue, setInternalValue] = useState(displayValue);

    useEffect(() => {
        setInternalValue(displayValue);
    }, [displayValue]);

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        const strippedValue = stripProtocol(newValue);
        setInternalValue(strippedValue);

        const fullUrl = addHttpsProtocol(newValue);

        props.onChange?.({
            ...event,
            target: {
                ...event.target,
                value: fullUrl
            }
        });
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const finalValue = event.target.value;
        setInternalValue(finalValue);

        const fullUrl = addHttpsProtocol(finalValue);

        props.onBlur?.({
            ...event,
            target: {
                ...event.target,
                value: fullUrl
            }
        } as React.FocusEvent<HTMLInputElement>);
    };

    return (
        <TextField
            {...props}
            type="url"
            maxLength={maxLength}
            value={internalValue}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment
                            position="start"
                            sx={{
                                marginRight: 0.25,
                                opacity: 0.75,
                                ...(props.readOnly && { color: 'text.secondary' })
                            }}
                        >
                            https://
                        </InputAdornment>
                    )
                }
            }}
        />
    );
};
